<template>
  <section class="calculator" v-if="state">
    <form name="calculator" @submit.prevent @reset.prevent="reset">
      <h2 class="calculator__title">
        <p-calculator-icon />
        {{ i18n._('Calculatrice de semences') }}
      </h2>
      <button type="reset" class="calculator__reset-button">{{ i18n._('Réinitialiser') }}</button>
      <div class="calculator__grid" @keydown.capture="filterNumberInput">
        <p class="calculator-grid__measure-unit">{{ i18n._('Unité de mesure') }}</p>
        <adn-radio-group horizontal name="unit" :initial-value="state.unit" @selected-value="updateCalculatorDB">
          <template #default="{ onSelectedValue, groupName, initialValue }">
            <adn-radio-button
              v-bind="{ initialValue, groupName }"
              @input="onSelectedValue"
              value="MG"
              :disabled="!availableUnits.MG"
            >
              {{ i18n._('MG') }}
            </adn-radio-button>
            <adn-radio-button
              v-bind="{ initialValue, groupName }"
              @input="onSelectedValue"
              value="kg"
              :disabled="!availableUnits.kg"
            >
              {{ i18n._('kg') }}
            </adn-radio-button>
          </template>
        </adn-radio-group>
        <label for="calculator_surface" class="calculator-grid__label">
          <p>
            {{ i18n._('Surface') }}
            <b>({{ i18n._('ha') }})</b>
          </p>
        </label>
        <input
          type="number"
          inputmode="numeric"
          id="calculator_surface"
          min="0"
          v-model="state.surface"
          @input="updateCalculatorDB"
          :placeholder="i18n._('0')"
          required
        />
        <label for="calculator_density" class="calculator-grid__label">
          <p>
            {{ i18n._('Densité du semis') }}
            <b>({{ i18n._('G/m²') }})</b>
          </p>
        </label>
        <input
          type="number"
          inputmode="numeric"
          id="calculator_density"
          min="0"
          v-model="state.density"
          @input="updateCalculatorDB"
          :placeholder="i18n._('0')"
          required
        />
        <label for="calculator_pmg" class="calculator-grid__label">
          <p>
            {{ i18n._('PMG') }}
            <b>({{ i18n._('g') }})</b>
          </p>
        </label>
        <input
          type="number"
          inputmode="numeric"
          id="calculator_pmg"
          min="0"
          v-model="state.pmg"
          :required="state.unit === 'kg'"
          :placeholder="i18n._('0')"
          @input="updateCalculatorDB"
        />
      </div>
    </form>
    <section class="calculator__result">
      <p-convert-result
        :product-id="productId"
        :is-mounted-in-variant="false"
        :class="{ 'calculator-result__convert-result--border': !isNaN(result.MG) || !isNaN(result.kg) }"
      />
      <p class="calculator__calculation-result">
        <output v-if="!isNaN(result[state.unit])" name="resultMain">
          {{ formatMeasureUnit(result[state.unit], state.unit) }}
        </output>
        <output v-if="!isNaN(result[subUnit]) && result[subUnit]" name="resultSub">
          {{ `(${formatMeasureUnit(result[subUnit], subUnit)})` }}
        </output>
      </p>
      <adn-button
        v-if="isSellable"
        kind="link"
        class="calculator__see-offers"
        v-scroll-to="{
          el: '#product-detail__offer-list',
          offset: -130,
        }"
        data-test="see-offers"
      >
        <span class="adn-icon adn-icon--navigation-arrow-up"></span>
        <span class="calculator__btn-text">{{ i18n._('Retour aux offres') }}</span>
      </adn-button>
    </section>
  </section>
</template>

<script lang="ts">
import { formatMeasureUnit } from '@b2ag/l10n'
import { computed, defineComponent } from 'vue'
import { useProduct } from '@b2ag/catalog/src/useProduct'
import AdnRadioGroup from '@b2ag/design-system/src/components/AdnRadioButton/AdnRadioGroup.vue'
import AdnRadioButton from '@b2ag/design-system/src/components/AdnRadioButton/AdnRadioButton.vue'
import { i18n } from '@b2ag/locale'
import { useCalculator } from '../../composables/useCalculator'
import PCalculatorIcon from './PCalculatorIcon.vue'
import PConvertResult from '../PConvertResult/PConvertResult.vue'
import { getSubUnit } from './helpers'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'

export default defineComponent({
  name: 'PCalculator',
  components: {
    PCalculatorIcon,
    PConvertResult,
    AdnRadioGroup,
    AdnRadioButton,
    AdnButton,
  },
  props: {
    productId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { state, availableUnits, result, updateCalculatorDB, reset } = useCalculator(props.productId)
    const { isSellable } = useProduct(props.productId!)

    const subUnit = computed(() => getSubUnit(state.unit))

    // prevents input of invalid characters in number input
    const filterNumberInput = (event) => {
      const input = event.target
      if (input instanceof HTMLInputElement && input.type === 'number')
        if (!(event.metaKey || event.ctrlKey || /^[\d.,]$|.{2,}/.test(event.key))) {
          event.preventDefault()
        }
    }

    return {
      i18n,
      state,
      availableUnits,
      result,
      updateCalculatorDB,
      reset,
      isSellable,
      subUnit,
      formatMeasureUnit,
      filterNumberInput,
    }
  },
})
</script>

<style scoped lang="scss">
.calculator {
  display: flex;
  flex-direction: column;
  margin: 3rem 1.6rem 0;
  background-color: var(--color-white);
  border-radius: 8px;
  border: 1px solid var(--color-grey-light);

  &__title {
    font-size: var(--font-size-l);
    line-height: 2rem;
  }

  > * {
    padding: 1.6rem;
  }

  @media (min-width: $breakpoint-sm) {
    margin: 3rem 0 0;
    flex-direction: row;

    &__title {
      font-size: var(--font-size-xl);
      line-height: 3rem;
    }

    > * {
      padding: 2rem;
    }
  }
}

form {
  flex: 2 1 auto;
  display: grid;
  row-gap: 1rem;
  grid-template-rows: min-content 1fr min-content;
  grid-template-areas: 'title' 'inputs' 'reset';
  border-radius: var(--zero-on-mobile, 1rem 0 0 1rem);

  @media (min-width: $breakpoint-sm) {
    grid-template-rows: min-content 1fr;
    grid-template-columns: 1fr min-content;
    grid-template-areas:
      'title  reset'
      'inputs inputs';
  }

  > h1 {
    color: var(--color-main);
    font-size: 1.8rem;
    line-height: 1.1em;
  }
}

button[type='reset'] {
  grid-area: reset;
  border: 0 none;
  padding: 0;
  margin: 0;
  background: transparent;
  color: inherit;
  text-decoration: underline;
}

input[type='number'] {
  border-radius: var(--border-radius-1);
  border: 1px solid var(--color-black);
  min-width: 5em;
  padding: 0.8rem;
  text-align: right;

  &:-moz-ui-invalid {
    box-shadow: none;
  }

  &:focus {
    box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
  }

  &:disabled {
    box-shadow: none;
    border-color: var(--color-inactive-light);
    color: var(--color-inactive-light);

    &:hover {
      box-shadow: none;
    }
  }
}

.calculator__grid {
  grid-area: inputs;
  display: grid;
  align-self: start;
  justify-content: space-between;
  row-gap: 0.5rem;
  grid-template-columns: 1fr 7.5em;

  > *:nth-child(odd) {
    grid-column: 1;
    margin-bottom: 0;
  }

  > *:nth-child(even) {
    grid-column: 2;
  }

  > .adn-radio-group {
    min-height: 4.2rem;
    grid-column: 1;

    @media (min-width: $breakpoint-sm) {
      justify-self: center;
      grid-column: 2;
    }
  }

  @media (width >= 768px) {
    grid-template-columns: unset;
    column-gap: 1rem;

    > *:nth-child(1),
    > *:nth-child(2) {
      grid-column: unset;
      grid-row: 1;
    }

    > *:nth-child(2n + 3) {
      grid-column: unset;
      grid-row: 2;
    }

    > *:nth-child(2n + 4) {
      grid-column: unset;
      grid-row: 3;
      text-align: right;
    }
  }

  @media (min-width: $breakpoint-lg) {
    align-self: center;

    > *:nth-child(odd) {
      grid-row: 1;
    }

    > *:nth-child(even) {
      grid-row: 2;
    }
  }
}

.calculator-grid__measure-unit {
  display: flex;
  align-items: center;
}

.calculator-grid__label {
  display: flex;
  align-items: center;

  @media (min-width: $breakpoint-md) {
    display: flex;
    align-items: flex-end;
  }
}

.calculator__result {
  text-align: center;
  color: var(--color-white);
  background-color: var(--color-main-dark);
  border-radius: 0 0 8px 8px;
  display: flex;
  flex-direction: column;

  output[name='resultSub'] {
    font-size: 1.2rem;
  }

  p {
    flex: 1;
  }

  @media (min-width: $breakpoint-sm) {
    flex: 0 0 20rem;
    border-radius: 0 8px 8px 0;
  }

  @media (min-width: $breakpoint-md) {
    flex: 0 0 24rem;
  }
}

.calculator__calculation-result {
  padding-top: 2rem;
  font-size: 1.8rem;
  min-height: 6.5rem;
  display: flex;
  flex-direction: column;
}

.calculator__result-separator {
  border-color: var(--color-white);
  width: 100%;
}

.calculator-result__convert-result--border {
  border-bottom: 1px solid var(--color-white);
}
</style>
<style lang="scss">
.calculator {
  &__reset-button {
    text-align: right;
    font-weight: 900;
  }
}

.calculator__see-offers.adn-button--link {
  outline: unset;

  > span.adn-button__text {
    text-decoration: none;
    color: var(--color-white);
    font-weight: normal;

    &:hover {
      color: var(--color-white);
    }
  }
}

.calculator__btn-text {
  text-decoration: underline;
}
</style>
